import React, { memo } from "react";

import { getLocalization } from "@utilities";
import { Alert, Icon } from "@partials";
import { AlertType, TabType } from "@constants";

import { ResultsListItemType, LocalizationTexts } from "../types";

type TargetType = "_blank" | "_self" | "_top";

type Props = {
  target?: TargetType;
  localization: LocalizationTexts;
  type: TabType;
  iconsList: any;
} & ResultsListItemType;

const ResultsListItem: React.FC<Props> = ({
  id,
  title,
  url,
  distanceKm,
  distanceUnit,
  openingHoursTime,
  openingHoursIcon,
  additionalInfo,
  isAdvisoryCenter,
  target,
  localization,
  type,
  iconsList,
  sourceItemId,
}) => {
  if (!title || !url) return null;

  const hasAdditionalInfo = additionalInfo !== "";
  const isTabTypeBranches = type === TabType.Branches;

  // icons
  const showIconOpeningHours = openingHoursIcon === "opening-hours";
  const { arrowRight, bank, branchAtmAtm, mapMarker, openingHours, nonstop, advisory } = iconsList;

  return (
    <li>
      <div className="mb-8 flex flex-col rounded bg-light shadow-tab sm:flex-row sm:items-center">
        <a
          href={url}
          title={title}
          target={target}
          data-testid={`result-item-${sourceItemId}`}
          className="group grow overflow-hidden rounded bg-white p-8 sm:px-12"
        >
          <div className="flex flex-col break-words text-lg sm:flex-row">
            <div className="w-full sm:w-2/3">
              <div className="mb-4 flex items-center">
                <h2 className="break-words text-xl font-semibold text-grey-200 group-hover:text-red-corporate group-hover:underline sm:text-2xl">
                  {title}
                  <Icon
                    name={arrowRight.name}
                    viewBox={arrowRight.viewbox}
                    alt={arrowRight.alt}
                    svg={arrowRight.svg}
                    className="icon ml-2 inline h-6 w-6 fill-red-corporate"
                  />
                </h2>
              </div>
              <div className={`${hasAdditionalInfo ? `mb-0` : `mb-2`} flex items-center sm:mb-0`}>
                <Icon
                  name={mapMarker.name}
                  viewBox={mapMarker.viewbox}
                  alt={mapMarker.alt}
                  svg={mapMarker.svg}
                  className="icon inline h-6 w-6 shrink-0 fill-red-corporate"
                />
                <div className="ml-2 break-words text-base">
                  {distanceKm} {distanceUnit}{" "}
                  {getLocalization(localization, "Branches_SharedDistanceNote")}
                </div>
              </div>
              {hasAdditionalInfo && (
                <div className="my-2 sm:mb-0 sm:mt-4">
                  <Alert
                    type={AlertType.Warning}
                    title={getLocalization(localization, "Branches_WarningAdditionalInfo")}
                    text={additionalInfo}
                    textAttributes={{ role: "alert", "aria-live": "assertive" }}
                    noPadding
                  />
                </div>
              )}
            </div>
            <div className="ml-0 flex w-full flex-col justify-center sm:ml-8 sm:w-1/3">
              <div className="mb-2 flex items-center">
                {isAdvisoryCenter ? (
                  <>
                    <Icon
                      name={advisory.name}
                      viewBox={advisory.viewbox}
                      alt={advisory.alt}
                      svg={advisory.svg}
                      className="icon inline h-6 w-6 shrink-0 fill-red-corporate"
                    />
                    <div className="ml-2 break-words text-base">
                      {getLocalization(localization, "Branches_Advisory")}
                    </div>
                  </>
                ) : (
                  <>
                    <Icon
                      name={isTabTypeBranches ? bank.name : branchAtmAtm.name}
                      viewBox={isTabTypeBranches ? bank.viewbox : branchAtmAtm.viewbox}
                      alt={isTabTypeBranches ? bank.alt : branchAtmAtm.alt}
                      svg={isTabTypeBranches ? bank.svg : branchAtmAtm.svg}
                      className="icon inline h-6 w-6 shrink-0 fill-red-corporate"
                    />
                    <div className="ml-2 break-words text-base">
                      {isTabTypeBranches
                        ? getLocalization(localization, "Branches_Branch")
                        : getLocalization(localization, "Branches_Atm")}
                    </div>
                  </>
                )}
              </div>
              <div className="mb-2 flex items-center">
                <Icon
                  name={showIconOpeningHours ? openingHours.name : nonstop.name}
                  viewBox={showIconOpeningHours ? openingHours.viewbox : nonstop.viewbox}
                  alt={showIconOpeningHours ? openingHours.alt : nonstop.alt}
                  svg={showIconOpeningHours ? openingHours.svg : nonstop.svg}
                  className="icon inline h-6 w-6 shrink-0 fill-red-corporate"
                />
                <div className="ml-2 break-words text-base">
                  {openingHoursTime}
                </div>
              </div>
              {isAdvisoryCenter ? (
                <div className="break-words text-base underline">
                  {getLocalization(localization, "Branches_AdvisoryDetail")}
                </div>
              ) : (
                <div className="break-words text-base underline">
                  {isTabTypeBranches
                    ? getLocalization(localization, "Branches_BranchDetail")
                    : getLocalization(localization, "Branches_AtmDetail")}
                </div>
              )}
            </div>
          </div>
        </a>
      </div>
    </li>
  );
};

export default memo(ResultsListItem);
